'use client';

import { useCallback, type ReactNode } from 'react';
import { HStack, token } from '@terra/ui-styled-system';
import {
  Button,
  IconButton,
  MetaDefault,
  Tooltip,
} from '@terra/ui-design-system';
import {
  CircleArrowUp02Icon,
  Download01Icon,
  MessageMultiple01Icon,
  LayoutBottomIcon,
  SparklesIcon,
} from '@terra/ui-icons';
import { useTranslations } from 'next-intl';
import { useProxyStore } from '../../store/proxy-store';
import {
  useSelectedCompany,
  useSelectedTarget,
  useTargetType,
} from '../../store/projectStore';
import {
  useBottomPanel,
  useLayoutStore,
  usePanelCollapse,
  useRightPanelView,
  usePanelVisibility,
} from '../../store/layoutStore';
import { type UseAutoUpdaterReturn } from '../../hooks/useAutoUpdater';
import { AUTO_UPDATER_OPEN_DIALOG_EVENT } from '../../constants/auto-updater';
import { useBottomRibbonStats } from '../../hooks/layout/useBottomRibbonStats';
import { useTargetTunnelRibbonState } from '../../hooks/layout/useTargetTunnelRibbonState';
import { ATTACK_SURFACES } from './target-selector/attackSurfaces';
import { BoothStationResetButton } from '../booth/BoothStationResetButton';
import { TunnelRibbonStatus } from './TunnelRibbonStatus';

type BottomRibbonProps = {
  autoUpdater: UseAutoUpdaterReturn;
};

function openRightPanelView(view: 'collaboration' | 'copilot'): void {
  const { showSidePanels, setRightPanelView, setChatCollapsed } =
    useLayoutStore.getState();

  if (!showSidePanels) {
    return;
  }

  setRightPanelView(view);
  setChatCollapsed(false);
}

function toggleRightPanelView(view: 'collaboration' | 'copilot'): void {
  const { isChatCollapsed, rightPanelView, setChatCollapsed } =
    useLayoutStore.getState();
  const isViewOpen = !isChatCollapsed && rightPanelView === view;

  if (isViewOpen) {
    setChatCollapsed(true);

    return;
  }

  openRightPanelView(view);
}

function StatusGroup({ children }: { children: ReactNode }) {
  return (
    <MetaDefault
      fontWeight="medium"
      color="fg.neutral.muted"
      whiteSpace="nowrap"
    >
      {children}
    </MetaDefault>
  );
}

export function BottomRibbon({ autoUpdater }: BottomRibbonProps) {
  const t = useTranslations('layout');
  const tAssets = useTranslations('aiApplications.assets');
  const tAutoUpdater = useTranslations('autoUpdater');
  const tEntities = useTranslations('proxy.filteredCounter.entities');
  const proxyPort = useProxyStore((state) => state.settings.port);
  const isInterceptOn = useProxyStore(
    (state) =>
      state.settings.interceptEnabled ||
      state.settings.interceptResponsesEnabled,
  );
  const {
    state: tunnelRibbonState,
    isPending: isTunnelPending,
    connectToRequiredNetwork,
    openTunnelRoutingSettings,
  } = useTargetTunnelRibbonState();
  const selectedCompany = useSelectedCompany();
  const selectedTarget = useSelectedTarget();
  const targetType = useTargetType();
  const { stats, isLoading: isStatsLoading } = useBottomRibbonStats();
  const {
    isCollapsed: isBottomPanelCollapsed,
    close,
    setTab,
  } = useBottomPanel();
  const { isChatCollapsed } = usePanelCollapse();
  const { rightPanelView } = useRightPanelView();
  const { showSidePanels } = usePanelVisibility();
  const { updateState, hasPendingUpdate, downloadProgress } = autoUpdater;
  const tenantName = selectedCompany?.name;
  const targetName = selectedTarget?.name;
  const surface = ATTACK_SURFACES.find(
    (candidate) => candidate.targetType === targetType,
  );
  const surfaceLabel = surface ? t(surface.labelKey) : undefined;
  const contextParts = [tenantName, targetName, surfaceLabel].filter(
    (part): part is string => Boolean(part),
  );
  const contextLabel =
    contextParts.length > 0 ? contextParts.join(' · ') : t('noTargetSelected');
  const statsLabel = stats
    ? [
        `${stats.assets} ${tEntities('assets')}`,
        `${stats.signals} ${tEntities('signals')}`,
        `${stats.executions} ${tEntities('executions')}`,
        `${stats.findings} ${tEntities('findings')}`,
      ].join(' · ')
    : isStatsLoading
      ? t('bottomRibbonStatsLoading')
      : null;
  const interceptLabel = isInterceptOn
    ? t('bottomRibbonInterceptOn')
    : t('bottomRibbonInterceptOff');
  const isBottomPanelOpen = !isBottomPanelCollapsed;
  const isCollaborationOpen =
    showSidePanels && !isChatCollapsed && rightPanelView === 'collaboration';
  const isCopilotOpen =
    showSidePanels && !isChatCollapsed && rightPanelView === 'copilot';
  const handleToggleBottomPanel = useCallback(() => {
    if (isBottomPanelOpen) {
      close();

      return;
    }

    setTab('terminal');
  }, [isBottomPanelOpen, close, setTab]);
  const handleToggleCollaboration = useCallback(() => {
    toggleRightPanelView('collaboration');
  }, []);
  const handleToggleCopilot = useCallback(() => {
    toggleRightPanelView('copilot');
  }, []);
  const handleOpenAutoUpdater = useCallback(() => {
    window.dispatchEvent(new Event(AUTO_UPDATER_OPEN_DIALOG_EVENT));
  }, []);
  const updateIcon =
    updateState === 'downloading' || updateState === 'preparing-download' ? (
      <Download01Icon size={12} />
    ) : (
      <CircleArrowUp02Icon size={12} />
    );

  return (
    <HStack
      h="full"
      w="full"
      pl="3"
      pr="1"
      gap="2"
      alignItems="center"
      justifyContent="space-between"
      bg="bg.neutral.light"
      borderTopWidth="1px"
      borderColor="border.subtle"
      overflow="hidden"
    >
      <HStack
        minW={0}
        overflow="hidden"
        alignItems="center"
        style={{ gap: '26px' }}
      >
        <StatusGroup>{contextLabel}</StatusGroup>
        {selectedTarget?.isInternalApp ? (
          <StatusGroup>{tAssets('internal')}</StatusGroup>
        ) : null}
        {statsLabel ? <StatusGroup>{statsLabel}</StatusGroup> : null}
        <StatusGroup>{interceptLabel}</StatusGroup>
        <TunnelRibbonStatus
          state={tunnelRibbonState}
          isPending={isTunnelPending}
          onConnect={connectToRequiredNetwork}
          onOpenRoutingSettings={openTunnelRoutingSettings}
        />
        <StatusGroup>{t('bottomRibbonPort', { port: proxyPort })}</StatusGroup>
      </HStack>

      <HStack gap="0" flexShrink={0} alignItems="center">
        <Tooltip
          title={
            isBottomPanelOpen ? t('closeBottomPanel') : t('openBottomPanel')
          }
        >
          <IconButton
            variant="ghost"
            size="xs"
            w="7"
            h="7"
            onClick={handleToggleBottomPanel}
            aria-label={
              isBottomPanelOpen ? t('closeBottomPanel') : t('openBottomPanel')
            }
            aria-pressed={isBottomPanelOpen}
            color={isBottomPanelOpen ? 'fg.default' : 'fg.muted'}
          >
            <LayoutBottomIcon size={16} />
          </IconButton>
        </Tooltip>

        <Tooltip title={t('collaboration')}>
          <IconButton
            variant="ghost"
            size="xs"
            w="7"
            h="7"
            onClick={handleToggleCollaboration}
            disabled={!showSidePanels}
            aria-label={t('collaboration')}
            aria-pressed={isCollaborationOpen}
            color={isCollaborationOpen ? 'fg.default' : 'fg.muted'}
          >
            <MessageMultiple01Icon size={16} strokeWidth={1.75} />
          </IconButton>
        </Tooltip>

        {hasPendingUpdate ? (
          <Tooltip
            title={
              updateState === 'downloading' && downloadProgress
                ? tAutoUpdater('downloadPercent', {
                    percent: downloadProgress.percent.toFixed(0),
                  })
                : tAutoUpdater('ribbonOpenDialog')
            }
          >
            <IconButton
              variant="ghost"
              size="xs"
              w="7"
              h="7"
              onClick={handleOpenAutoUpdater}
              aria-label={tAutoUpdater('ribbonOpenDialog')}
              color={updateState === 'downloaded' ? 'fg.error' : 'fg.default'}
            >
              {updateIcon}
            </IconButton>
          </Tooltip>
        ) : null}

        <BoothStationResetButton />

        <Button
          variant="solid"
          colorPalette="orange"
          size="xs"
          h="6"
          px="1.5"
          borderRadius="full"
          gap="1"
          borderWidth="1px"
          borderColor="border.accent"
          color="fg.onaccent.default"
          _hover={{ color: 'fg.onaccent.default' }}
          boxShadow="1px 2px 2px rgba(0, 0, 0, 0.05), inset 7px 4px 14px rgba(255, 255, 255, 0.2)"
          onClick={handleToggleCopilot}
          disabled={!showSidePanels}
          aria-label={t('copilot')}
          aria-pressed={isCopilotOpen}
        >
          <SparklesIcon
            size={14}
            strokeWidth={1.75}
            color={token('colors.fg.onaccent.default')}
          />
          <MetaDefault fontWeight="medium" color="fg.onaccent.default">
            {t('copilot')}
          </MetaDefault>
        </Button>
      </HStack>
    </HStack>
  );
}
